
'use client';

import { useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';
import { jobSearchTranslations } from '@/doi-tac-module/lib/i18n/bangladesh/job-search-translations';

/**
 * Unified translation hook that detects context (Agency vs Worker)
 * and provides a safe 't' function with fallbacks.
 */
export function useAppTranslation() {
  const { language } = useLanguage();
  const pathname = usePathname();

  const t = useCallback((key: string, replacements?: Record<string, any>): string => {
    const isAgency = pathname?.includes('/doi-tac') || pathname?.includes('/ho-so-cua-toi/bangladesh');
    
    let entry: any = null;

    if (isAgency) {
      // For agency, we might need a specific category or a deep search.
      // SearchModule specifically uses keys that we manually added to agencyTranslations.candidateSearch or hero.
      entry = (agencyTranslations as any).candidateSearch?.filters?.labels?.[key] || 
              (agencyTranslations as any).candidateSearch?.banner?.labels?.[key] ||
              (agencyTranslations as any).hero?.labels?.[key] ||
              (agencyTranslations as any).common?.[key] ||
              (jobSearchTranslations as any)[key]; // Fallback to worker translations if not found in agency
    } else {
      // Worker/Job search context
      entry = (jobSearchTranslations as any)[key];
    }

    if (!entry) {
        // Final fallback: try to find the key in either dictionary if specifically not found in preferred one
        entry = (jobSearchTranslations as any)[key] || (agencyTranslations as any).common?.[key];
    }

    if (!entry) return key;

    // Handle different entry structures
    let text = key;
    if (typeof entry === 'object' && entry !== null) {
        if (language in entry) {
            text = entry[language];
        } else if ('vi' in entry) {
            text = entry['vi']; // Default to Vietnamese if specific language missing
        } else if ('bn' in entry) {
            text = entry['bn'];
        }
    } else if (typeof entry === 'string') {
        text = entry;
    }

    // Replace variables (e.g., {count})
    if (replacements && typeof text === 'string') {
      Object.entries(replacements).forEach(([k, v]) => {
        text = text.replace(`{${k}}`, String(v));
      });
    }

    return text;
  }, [language, pathname]);

  return { t, language };
}
